import React from 'react'
import axios from 'axios'
import { API_POOL_INDEXED_BASE_URL } from 'constants/links'
import { Tooltip, AreaChart, Area, ResponsiveContainer } from 'recharts'
import { roundToDecimalPlace } from 'utils/data'
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartHeaderValue,
  ChartTitle,
  RechartsWrapper,
  StyledTabButton,
  Tabs,
  TooltipContainer,
} from './styled'
import { LoadingRows } from 'pages/Pool/styleds'

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
const Metrics = () => {
  const [dailyVolumes, setDailyVolumes] = React.useState<{ date: string; eth_volume: string }[]>([])
  const [ethPrices, setEthPrices] = React.useState<{ date: string; price: number }[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [selectedRange, setSelectedRange] = React.useState<'7D' | '30D'>('7D')

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API_POOL_INDEXED_BASE_URL}/api/volume`)
      const ethLast30DaysPrices = await axios.get(
        'https://api.coingecko.com/api/v3/coins/hyperliquid/market_chart?vs_currency=usd&days=30'
      )
      setDailyVolumes(response.data.volumes.dailyVolumes)
      // Filter and keep one price per day
      const dailyPrices = ethLast30DaysPrices.data.prices.reduce((acc: any[], price: any) => {
        const date = new Date(price[0])
        const dayKey = date.toISOString().split('T')[0] // Extract date in YYYY-MM-DD format

        // Ensure we only keep the first price for each day
        if (!acc.some((entry) => entry.date === dayKey)) {
          acc.push({ date: dayKey, price: price[1] })
        }
        return acc
      }, [])

      // Sort by date just in case
      const sortedDailyPrices = dailyPrices.sort(
        (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      setEthPrices(sortedDailyPrices)
      setLoading(false)
    } catch (error) {
      console.log(error)
    }
  }

  React.useEffect(() => {
    if (loading) {
      fetchMetrics()
    }
  }, [loading])

  let totalVolumeInRange = 0

  const chartData = dailyVolumes.map((volume: { date: string; eth_volume: string }) => {
    const volumeDate = volume.date
    const ethPrice = ethPrices.find((price: { date: string; price: number }) => price.date === volumeDate)
    const volumeAtDate = parseFloat(volume.eth_volume) * (ethPrice?.price ?? 0)
    totalVolumeInRange = totalVolumeInRange + volumeAtDate
    return {
      name: volume.date,
      volume: volumeAtDate,
    }
  })

  const chartDataFiltered = selectedRange === '7D' ? chartData.slice(0, -1).slice(-6) : chartData.slice(0, -1)

  return (
    <RechartsWrapper>
      {loading ? (
        <>
          <LoadingRows>
            <div />
            <div />
            <div />
            <div />
            <div />
          </LoadingRows>
        </>
      ) : (
        <>
          <ChartHeader>
            <ChartHeaderTitle>
              <ChartTitle>Volume (1D)</ChartTitle>
              <Tabs>
                <StyledTabButton onClick={() => setSelectedRange('7D')} isActive={selectedRange === '7D'}>
                  7D
                </StyledTabButton>
                <StyledTabButton onClick={() => setSelectedRange('30D')} isActive={selectedRange === '30D'}>
                  30D
                </StyledTabButton>
              </Tabs>
            </ChartHeaderTitle>
            <ChartHeaderValue>${roundToDecimalPlace(`${totalVolumeInRange}`, '0')}</ChartHeaderValue>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={185}>
            <AreaChart data={chartDataFiltered}>
              <defs>
                <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4AFB39" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#4AFB39" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="volume" stroke="#4AFB39" fill="url(#colorVolume)" />
              <Tooltip
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const data = payload[0].payload
                    return (
                      <TooltipContainer>
                        <span className="tooltip__heading">{formatDate(data.name)}</span>
                        <div className="tooltip__volume">
                          <span className="tooltip__volume-title">Volume</span>
                          <span className="tooltip__volume-value">${roundToDecimalPlace(data.volume, '0')}</span>
                        </div>
                      </TooltipContainer>
                    )
                  }
                  return null
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </RechartsWrapper>
  )
}

export default Metrics
